import React from "react";
import { Link } from "react-router-dom";
import { scrollToTop } from "src/shared/utils/appConstant";
import { ProductLogo } from "./productLogos";

export interface MarketplaceProduct {
  id: string;
  num: string;
  category: string;
  title: string;
  desc: string;
  bulletPoints: string[];
  exploreText: string;
  routeLink: string;
  themeColor: string;
  categoryColor: string;
  subNavLabel?: string;
}

interface ProductCardProps {
  product: MarketplaceProduct;
  className?: string;
  style?: React.CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  id?: string;
}

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16) || 0;
  const g = parseInt(cleaned.substring(2, 4), 16) || 0;
  const b = parseInt(cleaned.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  className = "",
  style,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
}) => {
  return (
    <div
      id={id}
      className={`product-card ${className}`}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Soft dashboard theme color gradient wash in background */}
      <div
        className="card-theme-glow"
        style={{
          background: `radial-gradient(circle at 88% 10%, ${hexToRgba(
            product.themeColor,
            0.07
          )} 0%, ${hexToRgba(product.themeColor, 0.02)} 42%, transparent 72%)`,
        }}
      />

      {/* Decorative contour waves matching Figma */}
      <svg
        className="card-contour-bg"
        viewBox="0 0 380 560"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <circle cx="440" cy="-80" r="220" stroke={product.themeColor} strokeOpacity="0.07" strokeWidth="1" />
        <circle cx="440" cy="-80" r="330" stroke={product.themeColor} strokeOpacity="0.055" strokeWidth="1" />
        <circle cx="440" cy="-80" r="450" stroke={product.themeColor} strokeOpacity="0.04" strokeWidth="1" />
        <circle cx="440" cy="-80" r="580" stroke={product.themeColor} strokeOpacity="0.03" strokeWidth="1" />
        <circle cx="440" cy="-80" r="720" stroke={product.themeColor} strokeOpacity="0.02" strokeWidth="1" />
      </svg>

      <div className="card-main-content">
        <div>
          <div className="card-top-row">
            <span
              className="card-category-pill"
              style={{ color: product.categoryColor }}
            >
              {product.category}
            </span>
            <span className="card-number">{product.num}</span>
          </div>
          <h3 className="card-title">{product.title}</h3>
          <p className="card-description">{product.desc}</p>
          <ul className="card-bullets">
            {product.bulletPoints.map((bullet, idx) => (
              <li key={idx}>
                <span
                  className="bullet-dot"
                  style={{ backgroundColor: product.themeColor }}
                />
                {bullet}
              </li>
            ))}
          </ul>
        </div>

        <Link
          to={product.routeLink}
          onClick={scrollToTop}
          className="card-cta-btn"
          style={{ color: product.themeColor }}
        >
          <span>{product.exploreText}</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="card-brand-spine">
        <ProductLogo id={product.id} vertical={true} />
      </div>
    </div>
  );
};

export default ProductCard;
